import React, { forwardRef, useCallback, useImperativeHandle, useRef, useState } from "react";
import { TimePicker, Tooltip, Space, Button, Popover, DatePicker } from "antd";
import { GlobalOutlined, CalendarOutlined, LockOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// 加载dayjs插件
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * UTC时间选择器组件
 *
 * 功能特性:
 * - 强制时区: 强制以UTC时区显示和处理时间，无论用户本地或全局时区设置如何。所有输入(value)和输出(onChange)都应为UTC时间。
 * - 时间格式: 支持自定义时间显示格式 (format)，默认为 "HH:mm"。
 * - 日期显示与编辑:
 *   - 可选显示日期部分 (`showDate` prop, 默认为 true)。日期按钮上显示 "MM-DD"。
 *   - 点击日期按钮可弹出日期选择器修改日期。
 *   - 可禁用日期编辑 (`disableDateEdit` prop, 默认为 false)。禁用后日期按钮呈锁定状态，不可点击。
 * - 默认日期 (`defaultDate` prop):
 *   - 当传入的 `value` 为 `null` 或 `undefined` 时:
 *     - 如果 `defaultDate` 有效，日期按钮将显示 `defaultDate` 的日期。
 *     - 用户选择时间时，所选时间将与 `defaultDate` 的日期部分合并（时分秒毫秒取自用户选择，日期取自 `defaultDate`，秒和毫秒会被重置为0）。
 *     - 日期选择弹窗中默认选中的是 `defaultDate`。
 *   - 如果 `value` 和 `defaultDate` 都无效，则日期部分不显示，选择时间时会使用当前UTC日期的零点作为基础日期。
 * - 时间选择与输入:
 *   - 支持通过下拉面板选择时间，步长为5分钟 (`minuteStep={5}`)。
 *   - 支持直接输入时间，例如输入 "400" 会按 HHmm 左补零归一化为 "04:00"；分钟仍按 minuteStep 取整。
 *   - 清空时间选择框会将 `value` 置为 `null`。
 * - 输出格式:
 *   - `onChange` 回调函数接收两个参数: `(dayjsUtcObject | null, utcTimeString | "")`。
 *   - `utcTimeString` 的格式可以通过 `outputFormat` prop 指定，默认为 ISO 8601 格式 "YYYY-MM-DDTHH:mm:ss.SSSZ"。
 * - 分钟步长: 通过 `minuteStep` prop 控制时间选择面板的分钟间隔和时间输入的取整行为 (默认为 5 分钟)。设为 1 则不取整。
 * - 日期动画: 通过 `dateAnimating` prop 控制日期部分的动画效果，当日期自动变化时可以使用该效果提示用户。
 * - 交互细节:
 *   - 时间部分总是显示 UTC 标识 (蓝色地球图标)。
 *   - Tooltip 会显示完整的 UTC 日期时间 (如果 `showDate` 为 true) 或仅显示 UTC 时间。
 *   - 如果 `value` 为空但 `defaultDate` 存在，Tooltip 会提示 "YYYY-MM-DD (时间未设置) UTC"。
 *
 * @param {object} props - 组件属性
 * @param {string | dayjs.Dayjs | null} props.value - 当前选中的UTC时间值。可以是符合 dayjs 解析的字符串或 dayjs 对象。
 * @param {function} props.onChange - 时间变化时的回调函数 `(dayjsUtcObject | null, utcTimeString | "") => void`。
 * @param {string} [props.format="HH:mm"] - antd TimePicker 的显示格式。
 * @param {object} [props.style] - 应用于根元素 Space.Compact 的样式。
 * @param {string} [props.placeholder] - TimePicker 的占位符。
 * @param {boolean} [props.disabled=false] - 是否禁用组件。
 * @param {string} [props.outputFormat="YYYY-MM-DDTHH:mm:ss.SSSZ"] - onChange 输出的 UTC 时间字符串格式。
 * @param {boolean} [props.showDate=true] - 是否显示日期部分。
 * @param {boolean} [props.disableDateEdit=false] - 是否禁用日期编辑功能。
 * @param {string | dayjs.Dayjs | null} [props.defaultDate] - 当 `value` 为空时使用的默认日期。用于显示日期和作为选择时间的基础。
 * @param {number} [props.minuteStep=5] - 分钟选择的步长，以及直接输入时的取整间隔。设为 1 表示不取整。
 * @param {boolean} [props.allowClear=true] - 是否显示清除按钮。
 * @param {boolean} [props.dateAnimating=false] - 是否显示日期部分的动画效果。
 * @param {boolean} [props.selectOnFocus=false] - 时间输入框获得焦点时是否选中完整文本。
 * @param {string} [props.popupClassName] - 统一附加到时间下拉、日期 Popover 与日期下拉的弹层根节点 className。
 */
export function normalizeTimeInputValue(inputValue, minuteStep = 1) {
    const trimmedValue = String(inputValue || "").trim();
    const colonMatch = trimmedValue.match(/^(\d{1,2}):(\d{2})$/);
    const compactMatch = /^\d{1,4}$/.test(trimmedValue) ? trimmedValue.padStart(4, "0") : null;

    const hoursText = colonMatch ? colonMatch[1] : compactMatch?.slice(0, 2);
    const minutesText = colonMatch ? colonMatch[2] : compactMatch?.slice(2, 4);
    if (!hoursText || !minutesText) {
        return null;
    }

    const hours = parseInt(hoursText, 10);
    let minutes = parseInt(minutesText, 10);
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
        return null;
    }

    const step = minuteStep > 0 ? minuteStep : 1;
    minutes = Math.floor(minutes / step) * step;

    return {
        hours,
        minutes,
        text: `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`,
    };
}

function joinClassNames(...classNames) {
    return classNames.filter(Boolean).join(" ") || undefined;
}

function mergePickerPopupClassName(classNames, popupClassName) {
    if (!popupClassName) {
        return classNames;
    }

    return {
        ...classNames,
        popup: {
            ...classNames?.popup,
            root: joinClassNames(classNames?.popup?.root, popupClassName),
        },
    };
}

const UTCTimePicker = forwardRef(({ value, onChange, format = "HH:mm", style, placeholder, disabled, outputFormat, showDate = true, disableDateEdit = false, defaultDate, allowClear = true, minuteStep = 5, dateAnimating = false, selectOnFocus = false, popupClassName, classNames, getPopupContainer, ...restProps }, ref) => {
    const rootRef = useRef(null);
    const timePickerRef = useRef(null);
    const timePickerClassNames = mergePickerPopupClassName(classNames, popupClassName);
    const datePickerClassNames = mergePickerPopupClassName(undefined, popupClassName);

    const runAfterPaint = useCallback((callback) => {
        if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
            window.requestAnimationFrame(callback);
            return;
        }
        callback();
    }, []);

    const getInputElement = useCallback(() => {
        // antd TimePicker exposes focus/blur but not select(); keep DOM lookup local to this wrapper.
        const input = rootRef.current?.querySelector?.("input");
        return input instanceof HTMLInputElement ? input : null;
    }, []);

    const selectInputText = useCallback(() => {
        const input = getInputElement();
        input?.select?.();
    }, [getInputElement]);

    const focusInput = useCallback((shouldSelect = false) => {
        timePickerRef.current?.focus?.();
        runAfterPaint(() => {
            const input = getInputElement();
            input?.focus?.();
            if (shouldSelect) {
                input?.select?.();
            }
        });
    }, [getInputElement, runAfterPaint]);

    // 处理 value 转换为 UTC dayjs 对象，处理纯时间字符串情况
    const initialUtcValue = value ? (typeof value === "string" && !value.includes("T") && !value.includes(" ") ? dayjs.utc(`1970-01-01T${value}`) : dayjs.utc(value)) : null;

    // 默认日期处理：取日期的开始时间点(00:00:00.000)
    const defaultDateUtc = defaultDate ? dayjs.utc(defaultDate).startOf("day") : null;

    // 显示用日期：优先使用实际值的日期，否则使用默认日期
    const displayDate = initialUtcValue ? initialUtcValue : defaultDateUtc;

    // 日期选择器的可见状态
    const [datePopoverVisible, setDatePopoverVisible] = useState(false);

    // 处理时间变更
    const handleChange = (time, timeString) => {
        if (!time) {
            onChange(null, "");
            return;
        }

        // 基础日期：优先用显示日期，否则用当天UTC零点
        const baseDate = displayDate || dayjs.utc().startOf("day");
        const hour = time.hour();
        // 根据 minuteStep 取整
        const step = minuteStep > 0 ? minuteStep : 1; // 避免步长为0或负数，至少为1
        const minute = Math.floor(time.minute() / step) * step;

        // 创建组合时间：日期+选择的时间，重置秒和毫秒
        const utcTime = baseDate.hour(hour).minute(minute).second(0).millisecond(0);

        // 格式化输出
        const fullFormat = outputFormat || "YYYY-MM-DDTHH:mm:ss.SSSZ";
        const utcTimeString = utcTime.format(fullFormat);

        onChange(utcTime, utcTimeString);
    };

    // 直接输入在控件层归一化，支持 HH:mm 与熟练工 HHMM 快捷输入。
    const tryCommitInputValue = useCallback((inputValue) => {
        if (format !== "HH:mm") {
            return false;
        }

        const normalizedInput = normalizeTimeInputValue(inputValue, minuteStep);
        if (!normalizedInput) {
            return false;
        }

        const baseDate = displayDate || dayjs.utc().startOf("day");
        const newTime = baseDate.hour(normalizedInput.hours).minute(normalizedInput.minutes).second(0).millisecond(0);
        handleChange(newTime, normalizedInput.text);
        return true;
    }, [displayDate, format, handleChange, minuteStep]);

    const commitCurrentInputValue = useCallback(() => {
        const input = getInputElement();
        return input ? tryCommitInputValue(input.value) : false;
    }, [getInputElement, tryCommitInputValue]);

    useImperativeHandle(ref, () => ({
        focus: () => focusInput(false),
        blur: () => timePickerRef.current?.blur?.(),
        select: selectInputText,
        focusAndSelect: () => focusInput(true),
        commitInputValue: commitCurrentInputValue,
    }), [commitCurrentInputValue, focusInput, selectInputText]);

    // 输入框失焦和按键处理：尝试提交直接输入值
    const handleBlur = (e) => {
        tryCommitInputValue(e.target.value);
        if (restProps.onBlur) restProps.onBlur(e);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" || e.key === "Tab") {
            tryCommitInputValue(e.target.value);
        }
        if (restProps.onKeyDown) restProps.onKeyDown(e);
    };

    const handleFocus = (e) => {
        if (selectOnFocus) {
            runAfterPaint(selectInputText);
        }
        if (restProps.onFocus) restProps.onFocus(e);
    };

    // 处理日期变更
    const handleDateChange = (date) => {
        if (!date) return;

        // 获取新日期(UTC零点)
        // 修正：从本地时区的 date 对象中提取 YYYY-MM-DD 字符串，然后将其直接解析为 UTC 日期，避免本地时区转换带来的错误
        const newDateUtc = dayjs.utc(date.format("YYYY-MM-DD"));

        // 保留原时间部分(如果有)，否则使用00:00:00.000
        const hour = initialUtcValue ? initialUtcValue.hour() : 0;
        const minute = initialUtcValue ? initialUtcValue.minute() : 0;
        const second = initialUtcValue ? initialUtcValue.second() : 0;
        const millisecond = initialUtcValue ? initialUtcValue.millisecond() : 0;

        // 合并新日期和时间部分
        const newUtcTime = newDateUtc.hour(hour).minute(minute).second(second).millisecond(millisecond);

        // 格式化输出
        const fullFormat = outputFormat || "YYYY-MM-DDTHH:mm:ss.SSSZ";
        const utcTimeString = newUtcTime.format(fullFormat);

        onChange(newUtcTime, utcTimeString);
        setDatePopoverVisible(false); // 关闭日期选择器
    };

    // 处理日历图标点击
    const handleCalendarClick = (e) => {
        if (disableDateEdit) return; // 禁用日期编辑时不响应
        e.stopPropagation();
        setDatePopoverVisible(true);
    };

    // 日期选择器弹窗内容
    const datePickerContent = (
        <div>
            <DatePicker
                value={displayDate ? dayjs.utc(displayDate) : null}
                onChange={handleDateChange}
                style={{ width: "100%" }}
                format="YYYY-MM-DD"
                classNames={datePickerClassNames}
                getPopupContainer={getPopupContainer}
            />
        </div>
    );

    // 渲染日期部分
    const renderDatePart = () => {
        if (!showDate || !displayDate) return null;

        // 生成日期按钮样式，支持动画
        const dateButtonStyle = {
            width: "80px",
            padding: "0 8px",
            textAlign: "center",
            transition: "all 0.5s ease",
            ...(dateAnimating
                ? {
                      backgroundColor: "#e6f7ff",
                      boxShadow: "0 0 8px rgba(24, 144, 255, 0.8)",
                      borderColor: "#1890ff",
                  }
                : {}),
        };

        // 禁用日期编辑时显示锁定状态
        if (disableDateEdit) {
            return (
                <Tooltip title="日期不可编辑">
                    <Button
                        type="default"
                        style={{
                            ...dateButtonStyle,
                            cursor: "not-allowed",
                        }}
                        disabled={true}
                    >
                        <Space>
                            <LockOutlined style={{ color: "#d9d9d9" }} />
                            <span style={{ fontSize: "12px", color: "#d9d9d9" }}>{displayDate.format("MM-DD")}</span>
                        </Space>
                    </Button>
                </Tooltip>
            );
        }

        // 可编辑日期状态
        return (
            <Popover
                content={datePickerContent}
                title="修改日期 (UTC)"
                trigger="click"
                open={datePopoverVisible}
                onOpenChange={setDatePopoverVisible}
                destroyOnHidden={true}
                rootClassName={popupClassName}
                getPopupContainer={getPopupContainer}
            >
                <Button type="default" style={dateButtonStyle} disabled={disabled} onClick={handleCalendarClick}>
                    <Space>
                        <CalendarOutlined style={{ color: "#1890ff" }} />
                        <span style={{ fontSize: "12px" }}>{displayDate.format("MM-DD")}</span>
                    </Space>
                </Button>
            </Popover>
        );
    };

    // 获取提示文本
    const getTooltipTitle = () => {
        // 有实际值时显示完整时间
        if (initialUtcValue) {
            return showDate ? initialUtcValue.format("YYYY-MM-DD HH:mm") + " UTC" : initialUtcValue.format("HH:mm") + " UTC";
        }
        // 无实际值但有默认日期时
        else if (displayDate && showDate) {
            return `${displayDate.format("YYYY-MM-DD")} (时间未设置) UTC`;
        }
        return "无时间";
    };

    return (
        <span ref={rootRef} style={{ display: "inline-flex", width: "100%" }}>
            <Space.Compact style={{ width: "100%", ...style }}>
                {renderDatePart()}
                <Tooltip title={getTooltipTitle()}>
                    <TimePicker
                        {...restProps}
                        ref={timePickerRef}
                        value={initialUtcValue}
                        onChange={handleChange}
                        format={format}
                        style={{ flex: 1 }}
                        placeholder={placeholder || (showDate ? "选择时间(UTC)" : "输入时间(UTC)")}
                        disabled={disabled}
                        suffixIcon={<GlobalOutlined style={{ color: "#1890ff" }} />}
                        minuteStep={minuteStep}
                        needConfirm={false}
                        allowClear={allowClear}
                        showNow={false}
                        classNames={timePickerClassNames}
                        getPopupContainer={getPopupContainer}
                        onBlur={handleBlur}
                        onKeyDown={handleKeyDown}
                        onFocus={handleFocus}
                    />
                </Tooltip>
            </Space.Compact>
        </span>
    );
});

UTCTimePicker.displayName = "UTCTimePicker";

export default UTCTimePicker;
